import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Chip, IconButton, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, FileDownload as DownloadIcon, Print as PrintIcon,
  Savings as IncomeIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { accountingAPI } from '../../services/api';
import { money, money2, label, errorText } from '../../utils/format';
import { fmtDate, localDate } from '../../utils/dates';
import { printPage, esc } from '../../utils/print';
import { PAYMENT_MODES, amountInWords } from './common';

const emptyForm = () => ({ category: '', title: '', description: '', amount: '', incomeDate: localDate(), paymentMode: 'CASH', receivedFrom: '', receiptNo: '', transactionId: '', remarks: '' });

export default function IncomeTab({ range, categories, notify, onChanged }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await accountingAPI.getIncomes({ from: range.from, to: range.to })).data); }
    catch (e) { notify(errorText(e, 'Could not load income'), 'error'); }
  }, [range.from, range.to, notify]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => (cat === 'ALL' || r.category === cat)
    && (!q || `${r.title} ${r.receivedFrom || ''} ${r.receiptNo || ''}`.toLowerCase().includes(q.toLowerCase())));
  const total = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);
  const shown = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openDialog = (row = null) => {
    setEditing(row);
    setForm(row ? {
      category: row.category, title: row.title, description: row.description || '', amount: row.amount, incomeDate: row.incomeDate,
      paymentMode: row.paymentMode || 'CASH', receivedFrom: row.receivedFrom || '', receiptNo: row.receiptNo || '', transactionId: row.transactionId || '', remarks: row.remarks || '',
    } : { ...emptyForm(), category: categories[0] || '' });
    setOpen(true);
  };

  const save = async () => {
    try {
      const data = { ...form, amount: form.amount === '' ? null : Number(form.amount) };
      if (editing) await accountingAPI.updateIncome(editing.id, data); else await accountingAPI.createIncome(data);
      notify(editing ? 'Income updated' : 'Income recorded');
      setOpen(false);
      load();
      onChanged();
    } catch (e) { notify(errorText(e, 'Could not save the income'), 'error'); }
  };

  const remove = async () => {
    try { await accountingAPI.deleteIncome(confirm.id); notify('Income entry deleted'); setConfirm(null); load(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  const exportExcel = () => {
    const sheet = filtered.map((r) => ({
      Date: r.incomeDate, 'Receipt no': r.receiptNo || '', Category: label(r.category), Title: r.title, 'Received from': r.receivedFrom || '',
      'Amount (₹)': Number(r.amount), Mode: label(r.paymentMode), 'Transaction id': r.transactionId || '', Remarks: r.remarks || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'Other income');
    XLSX.writeFile(wb, `other_income_${range.from}_to_${range.to}.xlsx`);
  };

  const printReceipt = (r) => {
    const body = `<div style="max-width:620px;margin:10px auto;border:1px solid #ccc;border-radius:10px;padding:22px 26px">
      <div style="display:flex;justify-content:space-between;margin-bottom:14px"><b>Receipt No: ${esc(r.receiptNo)}</b><span>Date: ${esc(fmtDate(r.incomeDate))}</span></div>
      <table><tbody>
        <tr><td style="width:38%"><b>Received from</b></td><td>${esc(r.receivedFrom || '-')}</td></tr>
        <tr><td><b>Towards</b></td><td>${esc(r.title)}${r.description ? ' - ' + esc(r.description) : ''}</td></tr>
        <tr><td><b>Category</b></td><td>${esc(label(r.category))}</td></tr>
        <tr><td><b>Payment mode</b></td><td>${esc(label(r.paymentMode))}${r.transactionId ? ' (' + esc(r.transactionId) + ')' : ''}</td></tr>
        <tr><td><b>Amount</b></td><td class="b" style="font-size:16px">${esc(money2(r.amount))}</td></tr>
        <tr><td><b>In words</b></td><td>${esc(amountInWords(r.amount))}</td></tr>
      </tbody></table>
      <div class="sig"><div>Received by</div><div>Authorised signatory</div></div></div>`;
    printPage('Money Receipt', `Receipt ${r.receiptNo || ''}`, body);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search title, payer, receipt no..." value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Category" value={cat} onChange={(e) => { setCat(e.target.value); setPage(0); }} sx={{ minWidth: 200 }}>
            <MenuItem value="ALL">All categories</MenuItem>
            {[...new Set([...categories, ...rows.map((r) => r.category)])].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!filtered.length}>Export Excel</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Record income</Button>
        </Box>
      </Box>
      <Chip sx={{ mb: 2, bgcolor: '#e8f5e9', color: '#00796b', fontWeight: 700 }} label={`Total: ${money(total)} across ${filtered.length} entr${filtered.length === 1 ? 'y' : 'ies'}`} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Student fee payments are recorded on the Fees page and appear automatically in the day book. Record everything else here: hostel fees, donations, grants, interest, uniform sales.
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Date</TableCell><TableCell>Receipt no</TableCell><TableCell>Category</TableCell><TableCell>Title</TableCell>
              <TableCell>Received from</TableCell><TableCell>Mode</TableCell><TableCell align="right">Amount</TableCell><TableCell align="center">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.incomeDate)}</TableCell>
                  <TableCell>{r.receiptNo || '-'}</TableCell>
                  <TableCell><Chip size="small" label={r.category} /></TableCell>
                  <TableCell>{r.title}</TableCell>
                  <TableCell>{r.receivedFrom || '-'}</TableCell>
                  <TableCell>{label(r.paymentMode) || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#00796b' }}>{money(r.amount)}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Print receipt"><IconButton size="small" onClick={() => printReceipt(r)}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                    <IconButton size="small" onClick={() => openDialog(r)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setConfirm(r)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>No other income recorded for this period</TableCell></TableRow>}
            </TableBody>
          </Table>
          <TablePagination component="div" count={filtered.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}><IncomeIcon /> {editing ? 'Edit income' : 'Record income'}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {[...new Set([...categories, form.category].filter(Boolean))].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.incomeDate} onChange={(e) => setForm({ ...form, incomeDate: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Amount (₹)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Payment mode" value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}>
                {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{label(m)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Received from" value={form.receivedFrom} onChange={(e) => setForm({ ...form, receivedFrom: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Receipt no (auto if empty)" value={form.receiptNo} onChange={(e) => setForm({ ...form, receiptNo: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Transaction / cheque id" value={form.transactionId} onChange={(e) => setForm({ ...form, transactionId: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete income entry?</DialogTitle>
        <DialogContent><Typography>{confirm?.title} ({money(confirm?.amount)}) will be removed from the books. This cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
